import { invalidateCachePartialMatch } from "@sg/query";
import { useCallback, useMemo } from "react";
import { useMutation, useQueries, useQuery } from "react-query";
import { z } from "zod";
import { agent } from "~/util/agent";
import { ENABLED_CURRENCIES } from "~/util/currencies";
import { CurrencySchema } from "./currency";

const BaseCurrencyPairSchema = z.object({
  id: z.string().optional(),
  enabled: z.boolean(),
  base_currency_name: z.string().min(1),
  counter_currency_name: z.string().min(1),
});

const CurrencyPairResourceSchema = BaseCurrencyPairSchema.extend({
  counter_currency: CurrencySchema,
  base_currency: CurrencySchema,
});

const CurrencyPairPriceResourceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  enabled: z.boolean().optional(),
  base_currency_name: z.string().min(1),
  counter_currency_name: z.string().min(1),
  timestamp: z.string().min(1),
  venue_name: z.string().min(1),
  bid: z.string().min(1),
  ask: z.string().min(1),
  mid: z.string().min(1),
  quote_currency: z.string().min(1),
});

type CurrencyPairInputType = z.infer<typeof BaseCurrencyPairSchema>;
type CurrencyPairResourceType = z.infer<typeof CurrencyPairResourceSchema>;
type CurrencyPairPriceResourceType = z.infer<typeof CurrencyPairPriceResourceSchema>;

const QUERY_CURRENCY_PAIR_KEY = "currency-pair";

const useCurrencyPair = (id: string) => {
  return useQuery([QUERY_CURRENCY_PAIR_KEY, id], async () =>
    agent(`instruments/currency_pairs/${id}`).json<CurrencyPairResourceType>()
  );
};

const useCurrencyPairs = () => {
  return useQuery([QUERY_CURRENCY_PAIR_KEY], async () =>
    agent("instruments/currency_pairs").json<CurrencyPairResourceType[]>()
  );
};

const useCurrencyPairsInfo = () => {
  const { data, refetch, dataUpdatedAt, isError, isLoading, isRefetching } = useCurrencyPairs();
  const enabledPairs = useMemo(() => {
    return (
      data
        ?.filter((d) => d.enabled)
        ?.filter((d) => ENABLED_CURRENCIES.includes(d.base_currency_name) && d.counter_currency_name === "USD") || []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt]);

  const result = useQueries(
    enabledPairs.map((p) => {
      return {
        queryKey: [QUERY_CURRENCY_PAIR_KEY, p.id],
        queryFn: () => agent(`instruments/currency_pairs/${p.id}/price`).json<CurrencyPairPriceResourceType>(),
        refetchInterval: 30000,
      };
    })
  );

  const refetchAll = useCallback(() => {
    refetch();
    result.forEach((result) => result.refetch());
  }, [refetch, result]);

  return useMemo(() => {
    return {
      isError: !!result.find((r) => r.isError) || isError,
      isLoading: !!result.find((r) => r.isLoading) || isLoading,
      isRefetching: !!result.find((r) => r.isRefetching) || isRefetching,
      refetch: refetchAll,
      dataUpdatedAt: Math.max(...result.map((r) => r.dataUpdatedAt)) || 0,
      data: result
        .flatMap((r) => {
          const [base_currency_name, counter_currency_name] = (r.data?.name || "").split("-");
          return {
            ...r.data,
            base_currency_name,
            counter_currency_name,
          };
        })
        .filter(Boolean) as CurrencyPairPriceResourceType[],
    };
  }, [isError, isLoading, isRefetching, refetchAll, result]);
};

const useCreateCurrencyPair = () => {
  return useMutation<CurrencyPairResourceType, Error, CurrencyPairInputType>(
    (data) => {
      return agent
        .post("instruments/currency_pairs", { labels: ["created", "currency_pair"], json: data })
        .json<CurrencyPairResourceType>();
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([QUERY_CURRENCY_PAIR_KEY]);
      },
    }
  );
};

export { BaseCurrencyPairSchema, useCreateCurrencyPair, useCurrencyPair, useCurrencyPairs, useCurrencyPairsInfo };
export type { CurrencyPairPriceResourceType };
